// ─────────────────────────────────────────────────────────────────────────
// Cuadre facturación ↔ producción (26-ago-2026, Fase A área contable).
//
// Semáforo por OT contra sus facturas vinculadas (NC restan):
//   🟢 cuadrada        — facturado == total OT
//   🔵 parcial         — OT abierta, facturado < total (normal con Rocmin)
//   🔴 con saldo       — OT CERRADA "facturado" pero facturó menos: plata
//                        potencialmente no cobrada. Lo urgente.
//   🟠 sobre-vinculada — facturado > total: factura multi-OT colgada de una
//                        sola (límite del modelo; se arregla en Fase B).
// Más las facturas SIN OT (trabajos que no pasaron por producción).
// Fuente: GET /sii/cuadre. Verificador espejo: D11 en check_invariants.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import backend from "../../backend";
import RepartoFactura from "./RepartoFactura";
import useIsMobile from "../../hooks/useIsMobile";
import { formatPrice } from "../../utils";

const fmt = n => `${n < 0 ? "−" : ""}$${formatPrice(Math.round(Math.abs(n || 0)))}`;
const fmtD = d => d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short" }) : "—";
const num = { fontVariantNumeric: "tabular-nums" };

const ESTILO = {
  con_saldo:        { icon: "🔴", label: "Cerrada con saldo",  color: "#e74c3c" },
  sobre_vinculada:  { icon: "🟠", label: "Sobre-vinculada",    color: "#f39c12" },
  parcial:          { icon: "🔵", label: "Parcial (abierta)",  color: "#3498db" },
  cuadrada:         { icon: "🟢", label: "Cuadrada",           color: "#2ecc71" },
};

const S = {
  card: { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
  // Tabla desktop (pulido 27-ago)
  th: {
    background: "#1f1f1f", padding: "8px 12px", textAlign: "left", fontSize: 10,
    fontWeight: 700, color: "#999", borderBottom: "1px solid #2e2e2e",
    textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 12px", color: "#bbb", borderBottom: "1px solid #252525",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
};

const Cuadre = () => {
  // Pulido desktop 27-ago: móvil intacto (tarjetas); desktop = tabla densa.
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState("problemas"); // problemas | todas | sin_ot
  // Qué factura se está repartiendo. Una a la vez: dos formularios abiertos sobre el
  // mismo descuadre es la forma más rápida de guardar dos repartos contradictorios.
  const [repartiendo, setRepartiendo] = React.useState(null);
  const [abierta, setAbierta] = useState(null);

  // Se extrae a `cargar` para poder REFRESCAR después de guardar un reparto: sin eso, el
  // descuadre que se acaba de arreglar seguiría en pantalla y daría la impresión de que
  // no pasó nada.
  const cargar = React.useCallback(() => {
    backend.get("sii/cuadre")
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.data?.error || e.message));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  if (error) return <div style={{ color: "#e74c3c", fontSize: 12, padding: 12 }}>Error: {error}</div>;
  if (!data) return <div style={{ color: "#8a8a8a", fontSize: 12, padding: 12 }}>Cuadrando facturación contra producción…</div>;

  const { resumen, filas, sinOT } = data;
  const visibles = filtro === "todas" ? filas
    : ["cuadrada", "con_saldo", "sobre_vinculada", "parcial"].includes(filtro)
      ? filas.filter(f => f.estado === filtro)
      : filas.filter(f => f.estado === "con_saldo" || f.estado === "sobre_vinculada" || f.estado === "parcial");

  // Tarjetas KPI CLIQUEABLES (feedback Maxi 27-ago): tocar filtra la lista.
  const kpis = [
    { key: "cuadrada",        label: "Cuadradas",        valor: resumen.cuadradas,        color: "#2ecc71", sub: "OTs peso a peso" },
    { key: "con_saldo",       label: "Con saldo",        valor: resumen.con_saldo,        color: "#e74c3c", sub: `${fmt(resumen.saldo_pendiente)} sin cobrar` },
    { key: "sobre_vinculada", label: "Sobre-vinculadas", valor: resumen.sobre_vinculadas, color: "#f39c12", sub: "factura multi-OT" },
    { key: "sin_ot",          label: "Sin OT",           valor: resumen.sin_ot,           color: "#a78bfa", sub: `${fmt(resumen.sin_ot_monto)} facturados` },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* KPIs del cuadre — cliqueables */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {kpis.map(k => (
          <div key={k.key}
            onClick={() => setFiltro(filtro === k.key ? "problemas" : k.key)}
            className="t-02 tap-card"
            style={{ ...S.card, flex: isMobile ? "1 1 42%" : "1 1 0", minWidth: 140, cursor: "pointer",
              border: `1px solid ${filtro === k.key ? k.color : "#2e2e2e"}` }}>
            <div style={S.label}>{k.label}{filtro === k.key ? " ✕" : ""}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: k.color, ...num }}>{k.valor}</div>
            <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 2, ...num }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Filtro */}
      <div className="chips-scroll" style={{ display: "flex", gap: 6 }}>
        {[
          ["problemas", "Con problemas + parciales"],
          ["todas", `Todas (${filas.length})`],
          ["sin_ot", `Facturas sin OT (${sinOT.length})`],
        ].map(([k, l]) => (
          <button key={k} type="button" className="chip-touch no-touch-min t-02"
            onClick={() => setFiltro(k)}
            style={{
              background: filtro === k ? "#3a1114" : "#1e1e1e",
              color: filtro === k ? "#ff8a8f" : "#999",
              border: `1px solid ${filtro === k ? "#e30613" : "#333"}`,
              borderRadius: 6, padding: "5px 12px", fontSize: 11, cursor: "pointer",
            }}>
            {l}
          </button>
        ))}
      </div>

      {filtro === "sin_ot" ? (
        isMobile ? (
        /* Facturas que no pasaron por producción (móvil: tarjetas) */
        sinOT.map(f => (
          <div key={f.folio} style={{ ...S.card, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontFamily: "monospace", fontWeight: 700, color: "#3498db", flexShrink: 0 }}>F{f.folio}</span>
            <span style={{ fontSize: 11, color: "#8a8a8a", flexShrink: 0, ...num }}>{fmtD(f.fecha)}</span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: "#b8b8b8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {f.razonReceptor || "—"}
            </span>
            <span style={{ fontWeight: 700, color: "#a78bfa", flexShrink: 0, ...num }}>{fmt(f.montoTotal)}</span>
          </div>
        ))
        ) : (
        /* Desktop: tabla de facturas sin OT */
        <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, overflowX: "auto", background: "#232323" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "90px" }} />
              <col style={{ width: "90px" }} />
              <col />
              <col style={{ width: "130px" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={S.th}>Folio</th>
                <th style={S.th}>Fecha</th>
                <th style={S.th}>Cliente</th>
                <th style={{ ...S.th, textAlign: "right" }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {sinOT.map((f, i) => (
                <tr key={f.folio}
                  style={{ background: i % 2 === 0 ? "#232323" : "#212121" }}
                  onMouseEnter={e => Array.from(e.currentTarget.cells).forEach(c => c.style.background = "#2a2a2a")}
                  onMouseLeave={e => Array.from(e.currentTarget.cells).forEach(c => c.style.background = "")}>
                  <td style={{ ...S.td, fontFamily: "monospace", fontWeight: 700, color: "#3498db" }}>F{f.folio}</td>
                  <td style={{ ...S.td, ...num }}>{fmtD(f.fecha)}</td>
                  <td style={{ ...S.td, color: "#fff" }}>{f.razonReceptor || "—"}</td>
                  <td style={{ ...S.td, textAlign: "right", fontWeight: 700, color: "#a78bfa", ...num }}>{fmt(f.montoTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )
      ) : !isMobile ? (
        /* Desktop: tabla densa de OTs con fila expandible de documentos */
        <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, overflowX: "auto", background: "#232323" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: "80px" }} />  {/* OT */}
              <col />                            {/* Cliente */}
              <col style={{ width: "170px" }} /> {/* Estado */}
              <col style={{ width: "125px" }} /> {/* Facturado */}
              <col style={{ width: "125px" }} /> {/* Total OT */}
              <col style={{ width: "125px" }} /> {/* Saldo */}
              <col style={{ width: "70px" }} />  {/* Docs */}
            </colgroup>
            <thead>
              <tr>
                <th style={S.th}>OT</th>
                <th style={S.th}>Cliente</th>
                <th style={S.th}>Estado</th>
                <th style={{ ...S.th, textAlign: "right" }}>Facturado</th>
                <th style={{ ...S.th, textAlign: "right" }}>Total OT</th>
                <th style={{ ...S.th, textAlign: "right" }}>Saldo</th>
                <th style={{ ...S.th, textAlign: "right" }}>Docs</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((f, i) => {
                const e = ESTILO[f.estado];
                const esAbierta = abierta === f.otNumber;
                const saldo = (f.totalOT || 0) - (f.facturado || 0);
                return (
                  <React.Fragment key={f.otNumber}>
                    <tr onClick={() => setAbierta(esAbierta ? null : f.otNumber)}
                      style={{ cursor: "pointer", background: esAbierta ? "#262626" : i % 2 === 0 ? "#232323" : "#212121" }}
                      onMouseEnter={ev => Array.from(ev.currentTarget.cells).forEach(c => c.style.background = "#2a2a2a")}
                      onMouseLeave={ev => Array.from(ev.currentTarget.cells).forEach(c => c.style.background = "")}>
                      <td style={{ ...S.td, color: "#fff", fontWeight: 700 }}>{f.otNumber}</td>
                      <td style={S.td}>{f.cliente || f.otName || "—"}</td>
                      <td style={{ ...S.td, color: e.color, fontWeight: 700, fontSize: 11 }}>{e.icon} {e.label}</td>
                      <td style={{ ...S.td, textAlign: "right", color: "#2ecc71", fontWeight: 700, ...num }}>{fmt(f.facturado)}</td>
                      <td style={{ ...S.td, textAlign: "right", ...num }}>{fmt(f.totalOT)}</td>
                      <td style={{ ...S.td, textAlign: "right", fontWeight: 700, ...num,
                        color: f.estado === "con_saldo" ? "#e74c3c" : f.estado === "sobre_vinculada" ? "#f39c12" : "#8a8a8a" }}>
                        {saldo === 0 ? "—" : fmt(saldo)}
                      </td>
                      <td style={{ ...S.td, textAlign: "right", color: "#8a8a8a", ...num }}>{(f.folios || []).length} {esAbierta ? "▲" : "▼"}</td>
                    </tr>
                    {esAbierta && (
                      <tr>
                        <td colSpan={7} style={{ padding: "8px 16px 12px", background: "#1f1f1f", borderBottom: "1px solid #2e2e2e" }}>
                          <div style={{ ...S.label, marginBottom: 6 }}>Documentos vinculados</div>
                          <div style={{ maxWidth: 640 }}>
                            {f.folios.map(d => (
                              <React.Fragment key={`${d.tipoDoc}-${d.folio}`}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, fontSize: 12, padding: "4px 0", borderBottom: "1px solid #282828" }}>
                                  <span style={{ fontFamily: "monospace", color: d.tipoDoc === 61 ? "#e74c3c" : "#3498db" }}>
                                    {d.tipoDoc === 61 ? "NC" : "F"}{d.folio} <span style={{ color: "#666" }}>· {fmtD(d.fecha)}</span>
                                    {d.repartida && <span style={{ color: "#a78bfa", marginLeft: 6 }}>repartida</span>}
                                  </span>
                                  <span style={{ flex: 1 }} />
                                  {/* D11 — antes esta pantalla mostraba el descuadre y no
                                      ofrecía manera de arreglarlo: había que anotarse el
                                      folio e ir a buscarlo a otra pantalla. */}
                                  {d._id && (
                                    <button
                                      type="button"
                                      onClick={() => setRepartiendo(p => p === d._id ? null : d._id)}
                                      style={{ background: "#a78bfa22", color: "#a78bfa", border: "1px solid #a78bfa55",
                                               borderRadius: 4, padding: "1px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}
                                    >
                                      {repartiendo === d._id ? "cerrar" : "repartir"}
                                    </button>
                                  )}
                                  <span style={{ color: d.tipoDoc === 61 ? "#e74c3c" : "#b8b8b8", ...num }}>
                                    {d.tipoDoc === 61 ? "−" : ""}{fmt(d.montoNeto)}
                                  </span>
                                </div>
                                {repartiendo === d._id && (
                                  <RepartoFactura
                                    documento={d}
                                    otActual={{ id: f.otId, numero: f.otNumber }}
                                    onListo={() => { setRepartiendo(null); cargar(); }}
                                    onCancelar={() => setRepartiendo(null)}
                                  />
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        visibles.map(f => {
          const e = ESTILO[f.estado];
          const esAbierta = abierta === f.otNumber;
          return (
            <div key={f.otNumber} className="t-02" style={{ ...S.card, padding: 0, overflow: "hidden", border: `1px solid ${esAbierta ? "#3a5a7a" : "#2e2e2e"}` }}>
              <div className={esAbierta ? "" : "tap-card"} onClick={() => setAbierta(esAbierta ? null : f.otNumber)}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", cursor: "pointer", minHeight: 48 }}>
                <span style={{ flexShrink: 0 }}>{e.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
                    OT {f.otNumber}
                    <span style={{ fontWeight: 400, color: "#8a8a8a", fontSize: 11 }}> · {f.cliente || f.otName || ""}</span>
                  </div>
                  <div style={{ fontSize: 10, color: e.color, fontWeight: 700 }}>{e.label}{f.estado === "con_saldo" ? ` — faltan ${fmt(f.saldo)}` : ""}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0, fontSize: 11, ...num }}>
                  <div style={{ color: "#2ecc71", fontWeight: 700 }}>{fmt(f.facturado)}</div>
                  <div style={{ color: "#8a8a8a" }}>de {fmt(f.totalOT)}</div>
                </div>
              </div>
              {esAbierta && (
                <div style={{ borderTop: "1px solid #2e2e2e", padding: "10px 14px", background: "#1f1f1f" }}>
                  <div style={{ ...S.label, marginBottom: 6 }}>Documentos vinculados</div>
                  {f.folios.map(d => (
                    <div key={`${d.tipoDoc}-${d.folio}`} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "4px 0", borderBottom: "1px solid #282828" }}>
                      <span style={{ fontFamily: "monospace", color: d.tipoDoc === 61 ? "#e74c3c" : "#3498db" }}>
                        {d.tipoDoc === 61 ? "NC" : "F"}{d.folio} <span style={{ color: "#666" }}>· {fmtD(d.fecha)}</span>
                      </span>
                      <span style={{ color: d.tipoDoc === 61 ? "#e74c3c" : "#b8b8b8", ...num }}>
                        {d.tipoDoc === 61 ? "−" : ""}{fmt(d.montoNeto)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default Cuadre;
